import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiArrowLeft,
  FiCheckCircle,
  FiClock,
  FiFileText,
  FiPlus,
  FiSave,
  FiShoppingBag,
  FiTruck,
  FiX,
} from "react-icons/fi";
import { apiRequest } from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import SalesOrderProductFilterModal from "./components/SalesOrderProductFilterModal";

const money = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

export default function CreateSalesOrder() {
  const navigate = useNavigate();
  const { accessToken, user } = useAuth();

  const [form, setForm] = useState({
    customerId: "",
    storeId: "",
    expectedDate: "",
    customerReference: "",
    customerNote: "",
  });

  const [customers, setCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState("");
  const [filterOptions, setFilterOptions] = useState(null);
  const [items, setItems] = useState([]);
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");

  const updateField = (field, value) => {
    setForm((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const loadCustomers = async (query = "") => {
    const params = new URLSearchParams();

    if (query.trim()) {
      params.set("query", query.trim());
    }

    const response = await apiRequest(
      `/sales-orders/customers?${params.toString()}`,
      { accessToken },
    );

    setCustomers(response);
  };

  useEffect(() => {
    async function loadInitialData() {
      try {
        setIsLoading(true);
        setError("");

        const [customerResponse, filterResponse] = await Promise.all([
          apiRequest("/sales-orders/customers", { accessToken }),
          apiRequest("/sales-orders/catalog/filters", { accessToken }),
        ]);

        setCustomers(customerResponse);
        setFilterOptions(filterResponse);
      } catch (requestError) {
        setError(
          requestError.message ||
            "Unable to load sales-order setup information.",
        );
      } finally {
        setIsLoading(false);
      }
    }

    if (accessToken) {
      loadInitialData();
    }
  }, [accessToken]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (accessToken) {
        loadCustomers(customerSearch).catch((requestError) => {
          setError(requestError.message || "Unable to search customers.");
        });
      }
    }, 350);

    return () => clearTimeout(timer);
  }, [customerSearch, accessToken]);

  const selectedCustomer = useMemo(
    () => customers.find((customer) => customer.id === form.customerId),
    [customers, form.customerId],
  );

  const selectedStore = useMemo(
    () =>
      (filterOptions?.stores || []).find(
        (store) => store.id === form.storeId,
      ),
    [filterOptions, form.storeId],
  );

  const isNormalLotAllocation =
    filterOptions?.lotAllocationMethod === "NORMAL";

  const subtotal = useMemo(
    () =>
      items.reduce(
        (total, item) =>
          total + Number(item.unitPrice || 0) * Number(item.quantity || 0),
        0,
      ),
    [items],
  );

  const totalQuantity = useMemo(
    () =>
      items.reduce(
        (total, item) => total + Number(item.quantity || 0),
        0,
      ),
    [items],
  );

  const estimatedGst = useMemo(
    () =>
      items.reduce((total, item) => {
        const lineAmount =
          Number(item.unitPrice || 0) * Number(item.quantity || 0);

        return total + (lineAmount * Number(item.gstRate || 0)) / 100;
      }, 0),
    [items],
  );

  const addProducts = (variants, filteredStoreId) => {
    if (
      filteredStoreId &&
      form.storeId &&
      filteredStoreId !== form.storeId
    ) {
      setError(
        "The filtered store differs from the selected fulfillment store. Select the same store before adding products.",
      );
      return;
    }

    if (filteredStoreId && !form.storeId) {
      updateField("storeId", filteredStoreId);
    }

    setItems((previous) => {
      const nextItems = [...previous];

      variants.forEach((variant) => {
        const existingIndex = nextItems.findIndex(
          (item) => item.variantId === variant.id,
        );

        if (existingIndex >= 0) {
          nextItems[existingIndex] = {
            ...nextItems[existingIndex],
            quantity: Number(nextItems[existingIndex].quantity) + 1,
          };
          return;
        }

        nextItems.push({
          variantId: variant.id,
          sku: variant.sku,
          barcode: variant.barcode,
          supplierBarcode: variant.supplierBarcode || null,
          productName: variant.product?.name || "Product",
          productCode: variant.product?.productCode || "",
          variantName: variant.variantName || "Default",
          colorName: variant.color?.name || "",
          sizeName: variant.size?.name || "",
          seasonName: variant.product?.season?.name || "",
          availableQuantity: Number(variant.availableQuantity || 0),
          unitPrice: Number(variant.sellingPrice ?? variant.mrp ?? 0),
          gstRate: Number(variant.product?.gstRate || 0),
          lots: variant.inventoryLots || [],
          selectedLotId: variant.inventoryLots?.[0]?.id || "",
          quantity: 1,
        });
      });

      return nextItems;
    });

    setError("");
  };

  const updateQuantity = (variantId, value) => {
    const requestedQuantity = Math.max(1, Number(value) || 1);

    setItems((previous) =>
      previous.map((item) => {
        if (item.variantId !== variantId) return item;

        const selectedLot = item.lots?.find(
          (lot) => lot.id === item.selectedLotId,
        );
        const maximum = isNormalLotAllocation
          ? Number(selectedLot?.remainingQuantity || 0) -
            Number(selectedLot?.reservedQuantity || 0)
          : item.availableQuantity;
        const quantity = Math.min(requestedQuantity, Math.max(1, maximum));

        if (quantity !== requestedQuantity) {
          setError(`Only ${maximum} unit(s) are available for ${item.sku}.`);
        }

        return { ...item, quantity };
      }),
    );
  };

  const updateSelectedLot = (variantId, inventoryLotId) => {
    setItems((previous) =>
      previous.map((item) => {
        if (item.variantId !== variantId) return item;

        const selectedLot = item.lots?.find((lot) => lot.id === inventoryLotId);
        const available = Math.max(
          1,
          Number(selectedLot?.remainingQuantity || 0) -
            Number(selectedLot?.reservedQuantity || 0),
        );

        return {
          ...item,
          selectedLotId: inventoryLotId,
          quantity: Math.min(Number(item.quantity), available),
          unitPrice: Number(selectedLot?.posPrice || item.unitPrice),
        };
      }),
    );
  };

  const removeItem = (variantId) => {
    setItems((previous) =>
      previous.filter((item) => item.variantId !== variantId),
    );
  };

  const createSalesOrder = async () => {
    if (!form.customerId) {
      setError("Please select a customer.");
      return;
    }

    if (!form.storeId) {
      setError("Please select a fulfillment store.");
      return;
    }

    if (!items.length) {
      setError("Add at least one product variant.");
      return;
    }

    try {
      setError("");
      setIsSaving(true);

      const order = await apiRequest("/sales-orders", {
        method: "POST",
        accessToken,
        body: {
          customerId: form.customerId,
          storeId: form.storeId,
          expectedDate: form.expectedDate || undefined,
          customerReference: form.customerReference.trim() || undefined,
          customerNote: form.customerNote.trim() || undefined,
          items: items.map((item) => ({
            variantId: item.variantId,
            quantity: Number(item.quantity),
            ...(isNormalLotAllocation
              ? {
                  requestedLots: [
                    {
                      inventoryLotId: item.selectedLotId,
                      quantity: Number(item.quantity),
                    },
                  ],
                }
              : {}),
          })),
        },
      });

      navigate(`/admindashboard/sales/orders/${order.id}`);
    } catch (requestError) {
      setError(requestError.message || "Unable to create sales order.");
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="h-10 w-10 rounded-full border-4 border-primary-100 border-t-primary-700 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <button
            onClick={() => navigate("/admindashboard/sales/orders/list")}
            className="flex items-center gap-2 text-gray-500 hover:text-primary-700 mb-3"
          >
            <FiArrowLeft />
            Back to Sales Orders
          </button>

          <h1 className="text-4xl font-bold">Create Sales Order</h1>

          <p className="text-gray-500 mt-2">
            Create a new order for a customer.
          </p>
        </div>

        <div className="flex gap-3">
          <button
            onClick={() => navigate("/admindashboard/sales/orders/list")}
            className="h-12 px-6 rounded-xl border border-gray-300 bg-white hover:bg-gray-50"
          >
            Cancel
          </button>

          <button
            disabled={isSaving}
            onClick={createSalesOrder}
            className="h-12 px-6 rounded-xl bg-primary-700 text-white flex items-center gap-2 disabled:opacity-60"
          >
            <FiSave />
            {isSaving ? "Creating..." : "Create Sales Order"}
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="grid lg:grid-cols-4 gap-5">
        <StatusCard
          title="Order Status"
          value="Draft"
          icon={<FiFileText size={22} />}
          color="bg-blue-50 text-blue-700"
        />

        <StatusCard
          title="Created By"
          value={
            user?.employee?.fullName ||
            user?.fullName ||
            user?.email ||
            "Current User"
          }
          icon={<FiCheckCircle size={22} />}
          color="bg-green-50 text-green-700"
        />

        <StatusCard
          title="Last Saved"
          value="Not saved yet"
          icon={<FiClock size={22} />}
          color="bg-orange-50 text-orange-700"
        />

        <StatusCard
          title="Store"
          value={selectedStore?.name || "Select store"}
          icon={<FiTruck size={22} />}
          color="bg-purple-50 text-purple-700"
        />
      </div>

      <div className="bg-white rounded-3xl border border-gray-100 p-8">
        <div className="flex items-center justify-between">
          <FlowStep title="Sales Order" active />
          <div className="h-1 flex-1 mx-4 bg-gray-200" />
          <FlowStep title="Delivery" />
          <div className="h-1 flex-1 mx-4 bg-gray-200" />
          <FlowStep title="Invoice" />
          <div className="h-1 flex-1 mx-4 bg-gray-200" />
          <FlowStep title="Payment" />
        </div>
      </div>

      <div className="bg-white rounded-3xl border border-gray-100 p-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h2 className="text-2xl font-bold">Order Information</h2>
            <p className="text-gray-500 mt-1">
              Basic information for this sales order.
            </p>
          </div>

          <span className="px-4 py-2 rounded-full bg-primary-50 text-primary-700 font-semibold">
            Auto Generated
          </span>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <Input
            label="Sales Order No."
            value="Generated after creation"
            disabled
          />

          <div>
            <label className="block text-sm font-medium text-gray-600 mb-2">
              Customer *
            </label>

            <input
              value={customerSearch}
              onChange={(event) => setCustomerSearch(event.target.value)}
              placeholder="Search by customer name, phone, or email..."
              className="w-full h-12 rounded-xl border border-gray-200 px-4 outline-none focus:border-primary-700"
            />

            <select
              value={form.customerId}
              onChange={(event) => updateField("customerId", event.target.value)}
              className="w-full mt-2 h-12 rounded-xl border border-gray-200 px-4 outline-none focus:border-primary-700 bg-white"
            >
              <option value="">Select customer</option>

              {customers.map((customer) => (
                <option key={customer.id} value={customer.id}>
                  {customer.fullName}
                  {customer.phone ? ` · ${customer.phone}` : ""}
                </option>
              ))}
            </select>
          </div>

          <Select
            label="Fulfillment Store *"
            value={form.storeId}
            onChange={(value) => updateField("storeId", value)}
            placeholder="Select store"
            options={filterOptions?.stores || []}
          />

          <Input
            label="Order Date"
            value={new Date().toISOString().slice(0, 10)}
            type="date"
            disabled
          />

          <Input
            label="Expected Delivery Date"
            value={form.expectedDate}
            type="date"
            onChange={(value) => updateField("expectedDate", value)}
          />

          <Input
            label="Customer Reference No."
            value={form.customerReference}
            placeholder="PO number, phone order, etc."
            onChange={(value) => updateField("customerReference", value)}
          />
        </div>
      </div>

      {selectedCustomer && (
        <div className="bg-white rounded-3xl border border-gray-100 p-6">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-5">
              <div className="h-20 w-20 rounded-3xl bg-primary-100 text-primary-700 flex items-center justify-center text-3xl font-bold">
                {selectedCustomer.fullName
                  .split(" ")
                  .map((word) => word[0])
                  .join("")
                  .slice(0, 2)}
              </div>

              <div>
                <div className="flex flex-wrap items-center gap-3">
                  <h2 className="text-2xl font-bold">
                    {selectedCustomer.fullName}
                  </h2>

                  <span className="px-3 py-1 rounded-full bg-green-100 text-green-700 text-xs font-semibold">
                    Active
                  </span>
                </div>

                <div className="flex flex-wrap gap-x-6 gap-y-2 mt-3 text-gray-500 text-sm">
                  {selectedCustomer.phone && (
                    <span>📞 {selectedCustomer.phone}</span>
                  )}

                  {selectedCustomer.email && (
                    <span>✉️ {selectedCustomer.email}</span>
                  )}

                  <span>
                    Reward Points:{" "}
                    {Number(
                      selectedCustomer.loyaltyAccount?.pointsBalance || 0,
                    ).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>

            <div className="rounded-2xl bg-primary-50 px-5 py-4">
              <p className="text-xs text-primary-700">Default Address</p>

              <p className="mt-1 text-sm font-medium text-gray-800">
                {selectedCustomer.addresses?.[0]
                  ? `${selectedCustomer.addresses[0].addressLine1}, ${selectedCustomer.addresses[0].city || ""}`
                  : "No saved customer address"}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-3xl border border-gray-100 p-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-2xl font-bold">Products</h2>
            <p className="text-gray-500 mt-2">
              Filter by brand, category, subcategory, season, product, and store.
            </p>
          </div>

          <button
            onClick={() => setIsProductModalOpen(true)}
            className="h-12 px-6 rounded-xl bg-primary-700 text-white flex items-center gap-2"
          >
            <FiPlus />
            Find & Add Products
          </button>
        </div>
      </div>

      <div className="bg-white rounded-3xl border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1350px]">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-5 py-4 text-left">Image</th>
                <th className="px-5 py-4 text-left">SKU</th>
                <th className="px-5 py-4 text-left">CHARMS barcode</th>
                <th className="px-5 py-4 text-left">Supplier / EAN</th>
                <th className="px-5 py-4 text-left">Product</th>
                <th className="px-5 py-4 text-left">Variant</th>
                {isNormalLotAllocation && (
                  <th className="px-5 py-4 text-left">Selected Lot</th>
                )}
                <th className="px-5 py-4 text-center">Available</th>
                <th className="px-5 py-4 text-center">Qty</th>
                <th className="px-5 py-4 text-right">Price</th>
                <th className="px-5 py-4 text-center">GST</th>
                <th className="px-5 py-4 text-right">Total</th>
                <th className="px-5 py-4 text-center">Action</th>
              </tr>
            </thead>

            <tbody>
              {items.length ? (
                items.map((item) => (
                  <tr
                    key={item.variantId}
                    className="border-b border-gray-100 hover:bg-gray-50"
                  >
                    <td className="px-5 py-4">
                      <div className="h-16 w-16 rounded-xl bg-gray-100 flex items-center justify-center">
                        <FiShoppingBag size={24} className="text-gray-400" />
                      </div>
                    </td>

                    <td className="px-5 py-4 font-semibold">{item.sku}</td>

                    <td className="px-5 py-4 font-mono text-xs">
                      {item.barcode || "—"}
                    </td>

                    <td className="px-5 py-4 font-mono text-xs text-gray-600">
                      {item.supplierBarcode || "—"}
                    </td>

                    <td className="px-5 py-4">
                      <p className="font-semibold">{item.productName}</p>
                      <p className="mt-1 text-xs text-gray-500">
                        {item.productCode || "—"}
                        {item.seasonName ? ` · ${item.seasonName}` : ""}
                      </p>
                    </td>

                    <td className="px-5 py-4 text-sm">
                      {[item.variantName, item.colorName, item.sizeName]
                        .filter(Boolean)
                        .join(" · ")}
                    </td>

                    {isNormalLotAllocation && (
                      <td className="px-5 py-4">
                        <select
                          value={item.selectedLotId}
                          onChange={(event) =>
                            updateSelectedLot(item.variantId, event.target.value)
                          }
                          className="h-10 max-w-[240px] rounded-lg border border-gray-200 bg-white px-3 text-sm outline-none focus:border-primary-700"
                        >
                          {(item.lots || []).map((lot) => {
                            const available =
                              Number(lot.remainingQuantity || 0) -
                              Number(lot.reservedQuantity || 0);

                            return (
                              <option key={lot.id} value={lot.id}>
                                {lot.lotNumber} · {money(lot.posPrice)} · {available} available
                              </option>
                            );
                          })}
                        </select>
                      </td>
                    )}

                    <td className="px-5 py-4 text-center">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          item.availableQuantity > 0
                            ? "bg-green-100 text-green-700"
                            : "bg-red-100 text-red-700"
                        }`}
                      >
                        {item.availableQuantity}
                      </span>
                    </td>

                    <td className="px-5 py-4">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() =>
                            updateQuantity(item.variantId, item.quantity - 1)
                          }
                          className="h-8 w-8 rounded-lg border border-gray-200"
                        >
                          −
                        </button>

                        <input
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(event) =>
                            updateQuantity(item.variantId, event.target.value)
                          }
                          className="h-10 w-16 rounded-lg border border-gray-200 text-center"
                        />

                        <button
                          onClick={() =>
                            updateQuantity(item.variantId, item.quantity + 1)
                          }
                          className="h-8 w-8 rounded-lg border border-gray-200"
                        >
                          +
                        </button>
                      </div>
                    </td>

                    <td className="px-5 py-4 text-right font-medium">
                      {money(item.unitPrice)}
                    </td>

                    <td className="px-5 py-4 text-center">{item.gstRate}%</td>

                    <td className="px-5 py-4 text-right font-bold">
                      {money(item.unitPrice * item.quantity)}
                    </td>

                    <td className="px-5 py-4 text-center">
                      <button
                        onClick={() => removeItem(item.variantId)}
                        className="h-10 w-10 rounded-xl bg-red-50 text-red-600 hover:bg-red-100 inline-flex items-center justify-center"
                      >
                        <FiX />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={isNormalLotAllocation ? 13 : 12} className="px-6 py-16 text-center text-gray-500">
                    No items added. Click “Find & Add Products” to begin.
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          <div className="border-t border-gray-200 bg-gray-50 px-6 py-5 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <button
              onClick={() => setIsProductModalOpen(true)}
              className="h-11 px-5 rounded-xl border border-dashed border-primary-700 text-primary-700 flex items-center gap-2"
            >
              <FiPlus />
              Add Another Product
            </button>

            <div className="flex gap-10">
              <MiniCard title="Total Items" value={items.length} />
              <MiniCard title="Total Quantity" value={totalQuantity} />
              <MiniCard title="Gross Amount" value={money(subtotal)} />
            </div>
          </div>
        </div>
      </div>

      <div className="grid xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2">
          <div className="bg-white rounded-3xl border border-gray-100 p-6">
            <h2 className="text-2xl font-bold mb-5">Customer Notes</h2>

            <textarea
              rows={6}
              value={form.customerNote}
              onChange={(event) =>
                updateField("customerNote", event.target.value)
              }
              placeholder="Delivery instructions or customer notes..."
              className="w-full rounded-2xl border border-gray-200 p-4 resize-none outline-none focus:border-primary-700"
            />
          </div>
        </div>

        <div>
          <div className="sticky top-6 bg-white rounded-3xl border border-gray-100 p-6">
            <h2 className="text-2xl font-bold">Order Summary</h2>

            <div className="space-y-5 mt-8">
              <Summary label="Items" value={items.length} />
              <Summary label="Quantity" value={totalQuantity} />
              <Summary label="Subtotal" value={money(subtotal)} />
              <Summary label="Estimated GST" value={money(estimatedGst)} />
            </div>

            <div className="border-t my-6" />

            <div className="flex justify-between gap-4">
              <span className="text-xl font-semibold">Grand Total</span>

              <span className="text-3xl font-bold text-primary-700">
                {money(subtotal)}
              </span>
            </div>

            <p className="mt-3 text-xs text-gray-500 leading-relaxed">
              Final pricing and GST are recalculated securely by the backend.
            </p>

            <div className="mt-6 rounded-2xl bg-primary-50 p-5">
              <h3 className="font-semibold text-primary-700">Workflow</h3>

              <div className="space-y-3 mt-4">
                <Status title="Draft" active />
                <Status title="Confirmed / Stock Reserved" />
                <Status title="Delivery" />
                <Status title="Invoice" />
                <Status title="Payment" />
              </div>
            </div>

            <button
              disabled={isSaving}
              onClick={createSalesOrder}
              className="w-full h-12 mt-8 rounded-xl bg-primary-700 text-white font-medium disabled:opacity-60"
            >
              {isSaving ? "Creating Sales Order..." : "Create Sales Order"}
            </button>
          </div>
        </div>
      </div>

      <SalesOrderProductFilterModal
        open={isProductModalOpen}
        onClose={() => setIsProductModalOpen(false)}
        filterOptions={filterOptions}
        selectedStoreId={form.storeId}
        onAddItems={addProducts}
        accessToken={accessToken}
      />
    </div>
  );
}

function StatusCard({ title, value, icon, color }) {
  return (
    <div className="bg-white rounded-3xl border border-gray-100 p-5">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm text-gray-500">{title}</p>
          <h3 className="mt-2 text-xl font-bold truncate">{value}</h3>
        </div>

        <div className={`h-12 w-12 shrink-0 rounded-2xl flex items-center justify-center ${color}`}>
          {icon}
        </div>
      </div>
    </div>
  );
}

function FlowStep({ title, active = false }) {
  return (
    <div className="flex flex-col items-center min-w-[70px]">
      <div className={`h-14 w-14 rounded-2xl flex items-center justify-center font-bold ${active ? "bg-primary-700 text-white" : "bg-gray-100 text-gray-500"}`}>
        {title.charAt(0)}
      </div>

      <p className={`mt-3 text-xs sm:text-sm font-medium text-center ${active ? "text-primary-700" : "text-gray-500"}`}>
        {title}
      </p>
    </div>
  );
}

function Input({
  label,
  value,
  onChange,
  type = "text",
  placeholder = "",
  disabled = false,
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-600 mb-2">
        {label}
      </label>

      <input
        type={type}
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        onChange={(event) => onChange?.(event.target.value)}
        className="w-full h-12 rounded-xl border border-gray-200 px-4 outline-none focus:border-primary-700 disabled:bg-gray-50 disabled:text-gray-500"
      />
    </div>
  );
}

function Select({ label, value, onChange, options, placeholder }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-600 mb-2">
        {label}
      </label>

      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="w-full h-12 rounded-xl border border-gray-200 px-4 outline-none focus:border-primary-700 bg-white"
      >
        <option value="">{placeholder}</option>

        {options.map((option) => (
          <option key={option.id} value={option.id}>
            {option.name}
            {option.code ? ` (${option.code})` : ""}
          </option>
        ))}
      </select>
    </div>
  );
}

function MiniCard({ title, value }) {
  return (
    <div className="text-center">
      <p className="text-gray-500 text-sm">{title}</p>
      <h3 className="text-xl font-bold mt-1">{value}</h3>
    </div>
  );
}

function Summary({ label, value }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-gray-500">{label}</span>
      <span className="font-semibold text-right">{value}</span>
    </div>
  );
}

function Status({ title, active = false }) {
  return (
    <div className="flex items-center gap-3">
      <div className={`h-3 w-3 rounded-full ${active ? "bg-green-500" : "bg-gray-300"}`} />

      <span className={active ? "font-semibold text-primary-700" : "text-gray-500"}>
        {title}
      </span>
    </div>
  );
}
